import { CellContext, ColumnDef } from '@tanstack/react-table';
import { creatorbase } from '@round/api';
import { VirtualizedTable } from '@round/creatorbase-ui';
import { formatSnakeCaseToDisplay } from 'helpers';
import { PlatformIcon, platformLabels } from 'Modules/Plans/helpers';
import ProfileLink from '../../../ProfileLink/ProfileLink';
import StatusBadge, { StatusBadgeTone } from '../../../StatusBadge/StatusBadge';
import TruncatedText from '../../../TruncatedText/TruncatedText';
import { BulkUploadItemRow } from '../../helpers';
import styles from './UploadItemsTable.module.css';

type Context = CellContext<BulkUploadItemRow, unknown>;

/**
 * Same reading as the upload's own badge: grey while queued, blue while on its way, green once saved,
 * red if it failed.
 */
const STATUS_TONES: Record<creatorbase.CreatorBulkUploadItemStatus, StatusBadgeTone> = {
    PENDING: 'neutral',
    PROCESSING: 'info',
    CREATED: 'success',
    FAILED: 'danger',
};

// A row's height with the cell's padding around its content, plus the row's border. Rows are measured
// once rendered, so this only has to be close.
const ROW_HEIGHT_PX = 49;

/**
 * How an item got on: its handle, its status and its error, if it has one. The minimums fit the modal,
 * so the table only ever scrolls down.
 */
const columns: ColumnDef<BulkUploadItemRow>[] = [
    {
        header: 'Creator',
        id: 'creator',
        cell: CreatorCell,
        meta: { className: styles.flexCell },
        size: 180,
    },
    {
        header: 'Status',
        id: 'status',
        cell: StatusCell,
        size: 110,
    },
    {
        header: 'Error',
        id: 'error_message',
        cell: ErrorCell,
        meta: { className: styles.flexCell },
        size: 170,
    },
];

type Props = {
    rows: BulkUploadItemRow[];
};

/** Upload items in the order they were submitted. */
const UploadItemsTable = ({ rows }: Props) => (
    <VirtualizedTable
        columns={columns}
        data={rows}
        virtualizerOptions={{
            estimateSize: () => ROW_HEIGHT_PX,
            getItemKey: (index) => rows[index]?.id ?? index,
        }}
        containerClassName={styles.tableContainer}
    />
);

export default UploadItemsTable;

/** The handle, linking to the profile it was submitted as, with the platform it is on at the far end. */
function CreatorCell({ row: { original } }: Context) {
    return (
        <span className={styles.creatorCell}>
            <ProfileLink platform={original.platform} handle={original.identifier} />
            <PlatformIcon
                platform={original.platform}
                role="img"
                aria-hidden={false}
                aria-label={platformLabels[original.platform]}
            />
        </span>
    );
}

function ErrorCell({ row: { original } }: Context) {
    return <TruncatedText text={original.error_message} />;
}

function StatusCell({ row: { original } }: Context) {
    return <StatusBadge tone={STATUS_TONES[original.status]}>{formatSnakeCaseToDisplay(original.status)}</StatusBadge>;
}
